import { useState } from "react";
import { base44 } from "@/api/base44Client";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import {
  Calendar,
  Clock,
  Video,
  Users,
  Award,
  CheckCircle,
  Sparkles,
} from "lucide-react";
import Lottie from "lottie-react";
import talktomeAvatar from "@/assets/talktome-avata.json";
import WebinarForm from "@/components/webinar/WebinarForm";
import SuccessScreen from "@/components/webinar/SuccessScreen";

const n8nWebhook =
  "https://n8n.explorernet.com.br/webhook/6a363be6-e8b9-4355-b172-4482e0221f09";

interface WebinarConfig {
  titulo_webinar: string;
  subtitulo: string;
  data_evento: string;
  detalhes_treinamento: string;
  assunto_email: string;
  mensagem_email: string;
  mensagem_whatsapp: string;
  logo_url: string;
}

export default function WebinarInscricao() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [inscritoData, setInscritoData] = useState<any>(null);

  const { data: configs } = useQuery({
    queryKey: ["webinar-config"],
    queryFn: () => base44.entities.WebinarConfig.list(),
    initialData: [],
  });

  const config: WebinarConfig = configs[0] || {
    titulo_webinar: "Inscrição - TalkToMe Partners",
    subtitulo: "Seja um Partner TalkToMe e Monetize Implantando a Plataforma!",
    data_evento: "18 e 19 de dezembro de 2025",
    detalhes_treinamento:
      "Formato: Online Ao Vivo, via Google Meet\nCarga Horária: 12h (6h por dia, das 9h às 16h)\nDia 1: Foco Estratégico + Comercial + Fundamentos\nDia 2: Foco Técnico + Construção de Projetos",
    assunto_email: "🎉 Inscrição Confirmada - TalkToMe Partners",
    mensagem_email:
      "<h2>Parabéns! Sua inscrição foi confirmada!</h2><p>Estamos muito felizes em ter você conosco no treinamento TalkToMe Partners.</p><p>Em breve você receberá mais detalhes sobre o acesso ao treinamento.</p>",
    mensagem_whatsapp:
      "Olá! Sua inscrição no TalkToMe Partners foi confirmada! 🎉 Em breve você será adicionado à nossa comunidade oficial.",
    logo_url:
      "https://www.talktome.com.br/_next/image/?url=%2Fimages%2Flogo.png&w=256&q=75",
  };

  const handleSubmit = async (formData: any) => {
    setIsSubmitting(true);

    // Preparar dados para o n8n com os nomes corretos
    const n8nData = {
      name: formData.nome_completo,
      email: formData.email,
      phone: formData.telefone,
      cpf: formData.cpf,
    };

    // Enviar para webhook n8n
    try {
      await fetch(n8nWebhook, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(n8nData),
      });
    } catch (error) {}

    // Salvar inscrição
    const inscricao = await base44.entities.WebinarInscricao.create({
      ...formData,
      email_enviado: false,
    });

    // Enviar email
    const emailBody = `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
        <div style="text-align: center; margin-bottom: 30px;">
          ${
            config.logo_url
              ? `<img src="${config.logo_url}" alt="TalkToMe" style="max-width: 200px; height: auto;">`
              : ""
          }
        </div>
        <div style="background: linear-gradient(135deg, #FF3366, #FF6B35); padding: 3px; border-radius: 16px;">
          <div style="background: #1a1a2e; padding: 30px; border-radius: 14px; color: #ffffff;">
            <h1 style="color: #FF3366; margin-bottom: 20px;">Olá, ${
              formData.nome_completo
            }!</h1>
            ${config.mensagem_email}
            <hr style="border: none; border-top: 1px solid #333; margin: 30px 0;">
            <p style="color: #888; font-size: 14px;">Este email foi enviado automaticamente pelo sistema TalkToMe Partners.</p>
          </div>
        </div>
      </div>
    `;

    await base44.integrations.Core.SendEmail({
      to: formData.email,
      subject: config.assunto_email,
      body: emailBody,
    });

    // Atualizar status do email
    await base44.entities.WebinarInscricao.update(inscricao.id, {
      email_enviado: true,
    });

    setInscritoData({
      ...formData,
      mensagemWhatsapp: config.mensagem_whatsapp,
    });
    setSubmitted(true);
    setIsSubmitting(false);
  };

  const detalhesArray = config.detalhes_treinamento?.split("\n") || [];

  return (
    <div className="min-h-screen bg-[#0a0a0f] relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-transparent to-pink-900/20" />
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-purple-600/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-pink-600/10 rounded-full blur-3xl" />

      <div className="relative z-10 container mx-auto px-4 py-8 md:py-16">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12 mt-8"
          >
            <div className="inline-flex items-center gap-2 bg-white/5 backdrop-blur-sm border border-white/10 rounded-full px-4 py-2 mb-6">
              <Sparkles className="w-4 h-4 text-yellow-400" />
              <span className="text-white/80 text-sm">Vagas Limitadas</span>
            </div>

            <div className="flex items-center justify-center gap-2 mb-8">
              <img
                src="/talktome-logo.webp"
                alt="TalkToMe"
                className="h-8 md:h-10"
              />
              <div className="w-8 h-8 md:w-10 md:h-10">
                <Lottie animationData={talktomeAvatar} loop={true} />
              </div>
            </div>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
            {/* Left Side - Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="order-2 lg:order-1"
            >
              <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8 h-full">
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
                  {config.titulo_webinar}
                </h2>
                <p className="text-white/70 text-lg mb-6">{config.subtitulo}</p>

                {/* Detalhes do Treinamento */}
                <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-2xl p-6 border border-white/5 mb-8">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-pink-400" />
                    Detalhes do Treinamento
                  </h3>
                  <div className="space-y-3">
                    {detalhesArray.map((detalhe, i) => (
                      <p
                        key={i}
                        className="text-white/70 text-sm flex items-start gap-2"
                      >
                        <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                        {detalhe}
                      </p>
                    ))}
                  </div>
                </div>

                {/* Features */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-5 text-center">
                    <Video className="w-8 h-8 text-pink-400 mx-auto mb-2" />
                    <p className="text-white font-medium text-sm">
                      Online Ao Vivo
                    </p>
                    <p className="text-white/50 text-xs">Via Google Meet</p>
                  </div>
                  <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-5 text-center">
                    <Clock className="w-8 h-8 text-orange-400 mx-auto mb-2" />
                    <p className="text-white font-medium text-sm">
                      12h de Conteúdo
                    </p>
                    <p className="text-white/50 text-xs">6h por dia</p>
                  </div>
                  <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-5 text-center">
                    <Users className="w-8 h-8 text-purple-400 mx-auto mb-2" />
                    <p className="text-white font-medium text-sm">
                      Comunidade VIP
                    </p>
                    <p className="text-white/50 text-xs">Acesso exclusivo</p>
                  </div>
                  <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-5 text-center">
                    <Award className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
                    <p className="text-white font-medium text-sm">
                      Certificado
                    </p>
                    <p className="text-white/50 text-xs">Partner Oficial</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Right Side - Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="order-1 lg:order-2"
            >
              <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8 sticky top-8">
                {!submitted ? (
                  <>
                    <div className="text-center mb-6">
                      <h3 className="text-xl font-semibold text-white mb-2">
                        Formulário de Inscrição
                      </h3>
                      <p className="text-white/60 text-sm">
                        Preencha os dados abaixo para garantir sua vaga
                      </p>
                    </div>
                    <WebinarForm
                      onSubmit={handleSubmit}
                      isLoading={isSubmitting}
                    />

                    <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500/20 rounded-xl">
                      <p className="text-blue-300 text-sm">
                        Após o preenchimento, você será adicionado à nossa{" "}
                        <strong>Comunidade Oficial no WhatsApp</strong> em até
                        24 horas úteis.
                      </p>
                    </div>
                  </>
                ) : (
                  <SuccessScreen
                    nome={inscritoData?.nome_completo}
                    telefone={inscritoData?.telefone}
                    mensagemWhatsapp={inscritoData?.mensagemWhatsapp}
                  />
                )}
              </div>
            </motion.div>
          </div>

          {/* Footer */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="text-center mt-16 mb-6 text-white/40 text-sm"
          >
            <p>© 2025 TalkToMe. Todos os direitos reservados.</p>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
